import { observer } from 'mobx-react-lite';
import { Platform, StyleSheet, View } from 'react-native';
import useResponsiveScreen from '../../../hooks/useResponsiveScreen';
import { useStore } from '../../../contexts/StoreContext';
import { ProductModelInstance } from '../../../store/_models/product';
import { useCallback, useEffect, useMemo } from 'react';
import Datadog from '../../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';
import { neutrals } from 'src/theme/colors';
import PreviewUtilsBar from '@photosi/rn-components/product-preview/PreviewUtilsBar';
import { OverlayItem } from '@photosi/rn-components/product-preview/ProductPreview.types';
import { SharedValue, useDerivedValue } from 'react-native-reanimated';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { parseAnalyticsParamsByProduct } from 'src/_shared/analytics.utils';
import { GridItemContentFooterV3 } from '@photosi/rn-components/product-groups/grid-item-content/grid-item-content-footer-v3/GridItemContentFooterV3';
import { GridItemContentHeaderV3 } from '@photosi/rn-components/product-groups/grid-item-content/grid-item-content-header-v3/GridItemContentHeaderV3';
import { GridItemContentCenterV3 } from '@photosi/rn-components/product-groups/grid-item-content/grid-item-content-center-v3/GridItemContentCenterV3';
import { ProductPreviewV3 } from 'src/editor-v3/components/product-preview/ProductPreviewV3';
import { productTestID } from 'src/helpers/testIDs.helper';
import i18n from 'src/app/i18n';

export type ProductProps = {
  product: ProductModelInstance;
  id: string;
  index: number;
  scrollTo?: (index: number) => void;
  shouldShowPreviewUtilsBar?: boolean;
  shouldUseContainerDimensions?: boolean;
  isGrouped?: boolean;
  isModalEdit?: boolean;
  showOverlays?: SharedValue<boolean> | boolean;
  disabled?: boolean;
  showBadges?: boolean;
  onHandleMultiple?: (id: string, wasSelected: boolean) => void;
  onShowOptionMobile?: () => void;
  isHoverSV?: SharedValue<string>;
};

export const ProductV3 = observer(
  ({
    product,
    id,
    index,
    scrollTo,
    shouldShowPreviewUtilsBar = true,
    shouldUseContainerDimensions = false,
    isGrouped = false,
    isModalEdit = false,
    showOverlays = false,
    disabled = false,
    showBadges = true,
    onHandleMultiple,
    onShowOptionMobile,
    isHoverSV,
  }: ProductProps) => {
    const {
      project,
      project: { isPreviewModeActive, activeTool, isProductEditModeActive, activeProject, activeGroup },
    } = useStore();
    const { isDesktop } = useResponsiveScreen();
    const isEditModeActive = isModalEdit;
    const isSelected = product.isSelected;
    const isHighlightedSV = useDerivedValue(() => {
      return Boolean(!isModalEdit && isSelected);
    }, [isModalEdit, isSelected]);

    const select = useCallback(() => {
      const wasSelected = product.isSelected;
      product.setIsSelected(!wasSelected);
      onHandleMultiple?.(product.id, wasSelected);
      if (!wasSelected) {
        amplitude.trackEvent(AnalyticsEvents.personalize.photoSelectClick, {
          ...parseAnalyticsParamsByProduct(product),
          photo_id: product.id,
          has_quality_alert: product.userImages?.[0]?.qualityAlert,
        });
      }
    }, [product, onHandleMultiple]);

    const previewOverlayArray = useMemo<OverlayItem[]>(
      () => [
        {
          render: () => <PreviewUtilsBar index={index} product={product} />,
          position: 'top',
        },
      ],
      [index, product],
    );

    const onPressZoomInner = () => {
      amplitude.trackEvent(AnalyticsEvents.personalize.zoomClick);
      if (activeProject && product) {
        activeProject.setZoomedImage(product.id);
      }
    };

    const groupedOverlayArray = useMemo<OverlayItem[]>(() => {
      const groupedOverlayArray: OverlayItem[] = [];

      groupedOverlayArray.push({
        render: () => (
          <GridItemContentHeaderV3
            product={product}
            onShowOptionMobile={onShowOptionMobile}
            onSelect={select}
            onPressZoomInner={onPressZoomInner}
            index={index}
          />
        ),
        position: 'top',
        zIndex: 2,
      });

      groupedOverlayArray.push({
        render: () => <GridItemContentCenterV3 product={product} isHoverSV={isHoverSV} onSelect={select} />,
        position: 'center',
      });

      groupedOverlayArray.push({
        render: () => <GridItemContentFooterV3 product={product} index={index} />,
        position: 'bottom',
      });

      return groupedOverlayArray;
    }, [
      product,
      product.family,
      product?.choices?.format,
      product?.choices?.paper,
      product.type,
      product.id,
      product.isSelected,
      activeGroup?.selectedProducts?.length,
      select,
      index,
    ]);

    const overlayHandler = useMemo(() => {
      return () => {
        // Caso preview mode (non edit mode) con barra utils
        if (!isEditModeActive && shouldShowPreviewUtilsBar && isDesktop) return previewOverlayArray;

        // Caso edit mode con barra utils
        if (isEditModeActive && shouldShowPreviewUtilsBar && isDesktop) return previewOverlayArray;

        // Caso grouped (indipendente da edit mode)
        if (isGrouped) return groupedOverlayArray;

        return undefined;
      };
    }, [isEditModeActive, shouldShowPreviewUtilsBar, isDesktop, isGrouped, previewOverlayArray, groupedOverlayArray]);

    useEffect(() => {
      if (
        project.activeProject &&
        index === project.activeProject.products.length - 1 &&
        project.activeProject.addingProductCount > 0 &&
        project.activeProject.alreadyAddedProductCount === project.activeProject.addingProductCount
      ) {
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.frame_loading_time_with_photos, {
          addedProduct: project.activeProject.alreadyAddedProductCount,
        });
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.frame_addition_time);
      }
    }, []);

    const isActive = (!isDesktop || product.id === project.activeProduct?.id) && isEditModeActive;
    const shownFullScreen = (isEditModeActive && !isDesktop) || product.preview.isMultiphoto;

    const hoverHandlers =
      Platform.OS === 'web' && isHoverSV
        ? {
            onPointerEnter: () => (isHoverSV.value = product.id),
            onPointerLeave: () => (isHoverSV.value = ''),
          }
        : {};

    return (
      <View
        testID={productTestID(product.id)}
        accessibilityLabel={i18n.t('edit:photo-item', { number: index + 1 })}
        style={[
          styles.container,
          isGrouped && styles.containerGrouped,
          isDesktop ? styles.containerDesktop : styles.containerMobile,
          isActive && styles.disabledPressable,
          isModalEdit && styles.modalEditContainer,
          product.isUpdating && styles.updating,
        ]}
        {...hoverHandlers}
      >
        {product.preview && (
          <View key={`${id}-Preview-${product.id}`} style={styles.fullSizeWrapper}>
            <ProductPreviewV3
              productType={product.type}
              disabled={disabled}
              preview={product.preview}
              editable={isEditModeActive}
              showGrid={isEditModeActive && activeTool === ACTIVE_TOOLS.frame}
              disableSwapImageAnimation={isEditModeActive && activeTool === ACTIVE_TOOLS.adjustments}
              swappable={isPreviewModeActive || isProductEditModeActive}
              shownFullScreen={shownFullScreen}
              shouldUseContainerDimensions={shouldUseContainerDimensions}
              renderOverlays={overlayHandler}
              showOverlays={showOverlays}
              isHighlighted={isHighlightedSV}
              showBadges={showBadges}
              userTexts={product.userTexts}
            />
          </View>
        )}
      </View>
    );
  },
);

export const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  modalEditContainer: {
    width: '100%',
    height: '100%',
  },
  containerGrouped: {
    width: '100%',
    height: '100%',
  },
  fullSizeWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    // borderColor: "red",
    // borderWidth: 2,
  },
  containerDesktop: {},
  containerMobile: {
    flexDirection: 'column',
  },
  updating: {
    opacity: 0.5,
    pointerEvents: 'none', // Disable interaction when hidden
  },
  title: {
    textAlign: 'center',
    color: neutrals.neutralGrays.tarmac,
    fontWeight: 400,
    position: 'absolute',
    top: -20,
    fontSize: 15.37,
    lineHeight: 19.78,
    letterSpacing: 0.16,
    minHeight: 20,
  },
  titleDesktop: {
    color: '#7D7D82',
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: -0.24,
  },
  titleMobile: {
    color: neutrals.neutralGrays.tarmac,
    fontSize: 15.37,
    lineHeight: 19.78,
    letterSpacing: 0.16,
  },
  disabledPressable: {
    cursor: 'auto', // Prevents "pointer" cursor
  },
});
